<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :closable="false"
    :style="{ width: '72rem', maxWidth: '96vw' }"
    :content-style="{ maxHeight: '78vh', overflowY: 'auto' }"
  >
    <template #header>
      <div class="flex align-items-center gap-3">
        <span class="text-xl font-medium">
          Snippet Widget — {{ selectedSeller.name }}
        </span>
        <Tag
          :severity="isActiveSeller ? 'success' : 'secondary'"
          :value="isActiveSeller ? 'Active' : 'Not active'"
        />
      </div>
    </template>

    <div class="grid">
      <div class="col-12 lg:col-6 builder-pane">
        <Accordion :multiple="true" :active-index="openSections">
          <AccordionTab
            v-for="section in sections"
            :key="section.key"
            :header="section.label"
          >
            <div :class="section.inline ? 'flex flex-wrap gap-4' : ''">
              <div
                v-for="option in section.options"
                :key="option.group + option.key"
                :class="section.inline ? 'builder-color' : 'builder-row'"
              >
                <div class="flex align-items-center gap-2">
                  <span
                    :class="[
                      section.inline ? 'text-sm' : '',
                      isDisabled(option) ? 'text-color-secondary' : '',
                    ]"
                  >
                    {{ option.label }}
                  </span>
                  <i
                    class="pi pi-info-circle text-color-secondary text-sm"
                    v-tooltip.top="option.tooltip"
                  ></i>
                </div>

                <SelectButton
                  v-if="option.type === 'enum'"
                  v-model="model[option.group || 'widget'][option.key]"
                  :options="option.values"
                  option-label="label"
                  option-value="value"
                  :allow-empty="false"
                  :disabled="isDisabled(option)"
                />
                <InputSwitch
                  v-else-if="option.type === 'boolean' && !option.inverted"
                  v-model="model[option.group || 'widget'][option.key]"
                  :disabled="isDisabled(option)"
                />
                <InputSwitch
                  v-else-if="option.type === 'boolean'"
                  :model-value="!model[option.group || 'widget'][option.key]"
                  @update:model-value="
                    model[option.group || 'widget'][option.key] = !$event
                  "
                />
                <div v-else class="flex align-items-center gap-2 mt-1">
                  <ColorPicker
                    :model-value="colorFor(option)"
                    @update:model-value="setColor(option, $event)"
                  />
                  <InputText
                    v-model="model[option.group || 'widget'][option.key]"
                    placeholder="from cover"
                    class="w-8rem p-inputtext-sm"
                  />
                  <Button
                    icon="pi pi-times"
                    text
                    size="small"
                    :disabled="!model[option.group || 'widget'][option.key]"
                    v-tooltip.top="'Back to cover image colors'"
                    @click="model[option.group || 'widget'][option.key] = ''"
                  />
                </div>
              </div>
            </div>
          </AccordionTab>
        </Accordion>

        <div class="mt-4">
          <div class="flex align-items-center justify-content-between mb-2">
            <span class="font-medium">Embed code</span>
            <div class="flex align-items-center gap-2">
              <InputSwitch v-model="inlineOptionsIntoTag" />
              <span class="text-sm">Inline options</span>
              <i
                class="pi pi-info-circle text-color-secondary text-sm"
                v-tooltip.top="
                  'Puts the options into the publisher\'s markup, where they outrank what is saved here.'
                "
              ></i>
            </div>
          </div>
          <div class="embed-code border-1 border-round-md p-3 relative">
            <Button
              icon="pi pi-copy"
              text
              size="small"
              v-tooltip.bottom="'Copy'"
              class="absolute right-0 top-0"
              @click="codeToClipboard"
            />
            <pre ref="componentcodeElement" class="my-0">{{
              componentCode
            }}</pre>
          </div>
        </div>
      </div>

      <div class="col-12 lg:col-6">
        <div class="flex align-items-center gap-2 mb-2">
          <span class="font-medium">Preview</span>
          <Tag
            :severity="usesDemoPlacement ? 'warning' : 'info'"
            :value="usesDemoPlacement ? 'Demo seller' : 'Current seller'"
          />
          <i
            v-if="usesDemoPlacement"
            class="pi pi-info-circle text-color-secondary text-sm"
            v-tooltip.top="
              'This seller has no snippets generated yet, so the preview plays demo content. Your options still apply to it.'
            "
          ></i>
        </div>
        <div
          class="live-preview-container"
          :style="{
            background:
              previewModel.widget.theme === 'dark' ? '#000' : '#f5f5f5',
          }"
        >
          <iframe
            :key="previewKey"
            ref="previewFrame"
            :srcdoc="previewHtmlDoc"
            class="live-preview-frame"
          ></iframe>
        </div>
        <small class="block text-color-secondary mt-2">
          Drag the corner to resize — the widget fits whatever the publisher's
          slot gives it.
          <span v-if="previewSize.width" class="font-medium">
            {{ previewSize.width }} × {{ previewSize.height }}
          </span>
        </small>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center w-full mt-2">
        <Button
          label="Reset"
          size="small"
          text
          :disabled="!hasDeviations"
          @click="resetToDefaults"
        />
        <div class="flex gap-2">
          <Button label="Close" size="small" outlined @click="closeDialog" />
          <Button
            label="Save"
            size="small"
            :loading="saving"
            :disabled="!isDirty"
            @click="save"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onBeforeUnmount,
  defineProps,
  defineEmits,
} from "vue";
import axios from "axios";
import Dialog from "primevue/dialog";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputSwitch from "primevue/inputswitch";
import SelectButton from "primevue/selectbutton";
import ColorPicker from "primevue/colorpicker";
import Tag from "primevue/tag";
import { useToast } from "primevue/usetoast";
import { API_BASE_URL } from "../consts";
import {
  WIDGET_OPTION_SECTIONS,
  DEMO_PREVIEW_PLACEMENT,
  buildFormModel,
  toDocument,
  toPreviewAttributes,
  toInlineAttributes,
} from "../constants/widgetOptions";

const toast = useToast();

const props = defineProps({
  visible: { type: Boolean, default: false },
  selectedSeller: { type: Object, default: () => ({}) },
  partner: { type: Object, default: () => ({}) },
  token: { type: String, default: null },
});

const emit = defineEmits(["close-dialog", "options-saved"]);

const sections = WIDGET_OPTION_SECTIONS;
const openSections = sections
  .map((section, index) => (section.advanced ? null : index))
  .filter((index) => index !== null);

const model = ref(buildFormModel(null));
const savedDocument = ref(null);
const saving = ref(false);
const inlineOptionsIntoTag = ref(false);
const previewKey = ref(0);
const previewModel = ref(buildFormModel(null));
let previewTimer = null;

watch(
  model,
  () => {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(() => {
      previewModel.value = JSON.parse(JSON.stringify(model.value));
    }, 600);
  },
  { deep: true }
);

const isDisabled = (option) =>
  option.group === "fab" && option.key !== "hidden" && model.value.fab.hidden;

const isActiveSeller = computed(
  () => props.selectedSeller.status === "approved"
);

const carriedOver = computed(() =>
  props.selectedSeller.widget_options?.widget?.cta
    ? { widget: { cta: props.selectedSeller.widget_options.widget.cta } }
    : null
);

const currentDocument = computed(() =>
  toDocument(model.value, carriedOver.value)
);
const hasDeviations = computed(() => toDocument(model.value, null) !== null);
const isDirty = computed(
  () =>
    JSON.stringify(currentDocument.value) !==
    JSON.stringify(savedDocument.value)
);

const colorFor = (option) =>
  (model.value[option.group || "widget"][option.key] || "").replace("#", "");

const setColor = (option, value) => {
  model.value[option.group || "widget"][option.key] = value ? `#${value}` : "";
};

watch(
  () => model.value.widget.resume_from_preview,
  (resume) => {
    if (resume) model.value.widget.preload = true;
  }
);

watch(
  () => model.value.widget.preload,
  (preload) => {
    if (!preload) model.value.widget.resume_from_preview = false;
  }
);

const usesDemoPlacement = computed(
  () => !isActiveSeller.value || !props.selectedSeller.playlist_id
);

const previewPlacement = computed(() =>
  usesDemoPlacement.value
    ? DEMO_PREVIEW_PLACEMENT
    : { seller: props.partner.id, subseller: props.selectedSeller.id }
);

const buildTag = (attributes) =>
  `<jamx-widget\n  ${attributes.join(
    "\n  "
  )}\n></jamx-widget>\n\n<script src="https://player.jamx.ai/snippet/snippet-widget.js"><\/script>`;

const identityAttributes = computed(() => [
  `seller="${props.partner.id}"`,
  `subseller="${props.selectedSeller.id}"`,
  `domain="${props.selectedSeller.domain}"`,
]);

const componentCode = computed(() =>
  buildTag(
    inlineOptionsIntoTag.value
      ? [...identityAttributes.value, ...toInlineAttributes(model.value)]
      : identityAttributes.value
  )
);

const previewHtmlDoc = computed(() => {
  const attributes = [
    `seller="${previewPlacement.value.seller}"`,
    `subseller="${previewPlacement.value.subseller}"`,
    ...toPreviewAttributes(previewModel.value),
  ];
  const isDark = previewModel.value.widget.theme === "dark";
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    html, body { height: 100%; }
    body { margin: 0; padding: 0; background: ${isDark ? "#000" : "#f5f5f5"}; }
    jamx-widget { display: block; height: 100%; }
  </style>
</head>
<body>
  ${buildTag(attributes)}
</body>
</html>`;
});

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) return;
    clearTimeout(previewTimer);
    model.value = buildFormModel(props.selectedSeller.widget_options ?? null);
    previewModel.value = JSON.parse(JSON.stringify(model.value));
    savedDocument.value = currentDocument.value;
    inlineOptionsIntoTag.value = false;
    previewKey.value += 1;
  },
  { immediate: true }
);

const previewFrame = ref(null);
const previewSize = ref({ width: 0, height: 0 });
let previewObserver = null;

watch(previewFrame, (element) => {
  previewObserver?.disconnect();
  previewObserver = null;
  if (!element || typeof ResizeObserver === "undefined") return;

  previewObserver = new ResizeObserver(([entry]) => {
    previewSize.value = {
      width: Math.round(entry.contentRect.width),
      height: Math.round(entry.contentRect.height),
    };
  });
  previewObserver.observe(element);
});

onBeforeUnmount(() => previewObserver?.disconnect());

function resetToDefaults() {
  model.value = buildFormModel(null);
}

async function save() {
  saving.value = true;
  try {
    await axios.put(
      `${API_BASE_URL}/sellers/${props.selectedSeller.id}/widget-options`,
      { widget_options: currentDocument.value },
      {
        headers: {
          Authorization: `Bearer ${
            props.token || localStorage.getItem("adminToken")
          }`,
        },
      }
    );
    savedDocument.value = currentDocument.value;
    emit("options-saved", currentDocument.value);
    toast.add({
      severity: "success",
      summary: "Saved",
      life: 4000,
    });
  } catch (error) {
    console.error("Error saving widget options:", error);
    toast.add({
      severity: "error",
      summary: "Not saved",
      detail: error.response?.data?.message || "Failed to save widget options.",
      life: 8000,
    });
  } finally {
    saving.value = false;
  }
}

function closeDialog() {
  emit("close-dialog");
}

function codeToClipboard() {
  navigator.clipboard.writeText(componentcodeElement.value.innerText);
}

const componentcodeElement = ref(null);
</script>

<style scoped>
.builder-pane {
  align-self: flex-start;
}

.builder-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 0;
}

.builder-row + .builder-row {
  border-top: 1px solid var(--surface-border);
}

.builder-color {
  min-width: 12rem;
}

.embed-code {
  background: var(--surface-ground);
  overflow-x: auto;
}

.live-preview-container {
  box-sizing: border-box;
  border: 2px solid var(--surface-border);
  border-radius: 8px;
  padding: 1rem;
  width: 429px;
  height: 888px;
  min-width: 386px;
  min-height: 604px;
  resize: both;
  overflow: auto;
}

.live-preview-frame {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}
</style>
